import React from "react";
import PropTypes from "prop-types";

import { getRoleInitials, getRoleLabel } from "../helpers";
import RoleFields from "../RoleFields";
import ShiftConfiguration from "../ShiftConfiguration";
import { UIAccordion, UIButton, UIIcon } from "../../../../../ui";
import { Typography } from "@mui/material";
import messages from "../../../../../../assets/locale/messages";
import { useSelector } from "react-redux";
import { clx } from "../../../../../../utils/Helpers";

const CustomTitle = ({
  role,
  rolesOptions,
  roleIndex,
  onRemoveRole,
  labels,
  removeDisabled,
}) => {
  const roleLabel = getRoleLabel(role, rolesOptions, labels, roleIndex);
  return (
    <div className="role-editor__header">
      <div className="role-editor__title">
        <span>{getRoleInitials(roleIndex)}</span>
        <Typography className="role-editor__heading text-heading-md" component="h2">
          {roleLabel}
        </Typography>
      </div>
      <div className="role-editor__actions">
        <UIButton
          className="role-editor__remove"
          type="button"
          onClick={onRemoveRole}
          variant="text"
          color="error"
          disabled={removeDisabled}
        >
          <UIIcon name="trash" size="sm" />
          {labels.removeRole}
        </UIButton>
      </div>
    </div>
  );
};

const RoleEditor = ({
  formik,
  role,
  roleIndex,
  rolesOptions,
  activeShiftIndex,
  accordionExpanded,
  onAccordionExpandedChange,
  onRemoveRole,
  onShiftChange,
  onAddShift,
  onRemoveShift,
  onTrainingToggle,
  defaultTrainingBoundaryMeters,
  hasError,
  removeRoleDisabled,
  portalCompanyOngoingEdit,
  companyPortalMode,
  isEditMode,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events } = messages[lang];
  const t = events?.creatEvent?.defineRolesAndShifts;

  return (
    <UIAccordion
      hasCustomizeTitle
      expanded={accordionExpanded}
      onChange={onAccordionExpandedChange}
      classNames={{
        root: clx({ "role-editor-accordion--error": hasError }),
      }}
      title={
        <CustomTitle
          roleIndex={roleIndex}
          onRemoveRole={onRemoveRole}
          role={role}
          rolesOptions={rolesOptions}
          labels={t}
          removeDisabled={removeRoleDisabled}
        />
      }
    >
      <div className="role-editor">
        <div className="role-editor__body">
          <RoleFields
            formik={formik}
            role={role}
            roleIndex={roleIndex}
            options={rolesOptions}
          />

          <div className="role-editor__divider" />

          <ShiftConfiguration
            formik={formik}
            role={role}
            roleIndex={roleIndex}
            activeShiftIndex={activeShiftIndex}
            onShiftChange={onShiftChange}
            onAddShift={onAddShift}
            onRemoveShift={onRemoveShift}
            onTrainingToggle={onTrainingToggle}
            defaultTrainingBoundaryMeters={defaultTrainingBoundaryMeters}
            hasError={hasError}
            portalCompanyOngoingEdit={portalCompanyOngoingEdit}
            companyPortalMode={companyPortalMode}
            isEditMode={isEditMode}
          />
        </div>
      </div>
    </UIAccordion>
  );
};


export default RoleEditor;

RoleEditor.propTypes = {
  formik: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  roleIndex: PropTypes.number.isRequired,
  rolesOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  activeShiftIndex: PropTypes.number.isRequired,
  accordionExpanded: PropTypes.bool.isRequired,
  onAccordionExpandedChange: PropTypes.func.isRequired,
  onRemoveRole: PropTypes.func.isRequired,
  onShiftChange: PropTypes.func.isRequired,
  onAddShift: PropTypes.func.isRequired,
  onRemoveShift: PropTypes.func.isRequired,
  onTrainingToggle: PropTypes.func.isRequired,
  defaultTrainingBoundaryMeters: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  hasError: PropTypes.bool,
  removeRoleDisabled: PropTypes.bool,
  portalCompanyOngoingEdit: PropTypes.bool,
  companyPortalMode: PropTypes.bool,
  isEditMode: PropTypes.bool,
};

RoleEditor.defaultProps = {
  hasError: false,
  removeRoleDisabled: false,
  portalCompanyOngoingEdit: false,
  companyPortalMode: false,
  isEditMode: false,
};

CustomTitle.propTypes = {
  roleIndex: PropTypes.number.isRequired,
  onRemoveRole: PropTypes.func.isRequired,
  labels: PropTypes.object.isRequired,
  role: PropTypes.object.isRequired,
  rolesOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  removeDisabled: PropTypes.bool,
};

CustomTitle.defaultProps = {
  removeDisabled: false,
};
